import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Form, Alert, Badge, Spinner } from 'react-bootstrap';
import { useBuilding } from '../../Helpers/BuildingContext';
import { BuildingCheckTemplate, INTERVAL_UNITS } from './types';
import { BuildingCheck, IntervalUnit, formatInterval } from './buildingChecksUtils';
import { translateInspectionCategory, translateRegulatoryType } from '../../types/EnumTranslations';
import { buildingChecksApi, BuildingCheckChanges } from '../../api/buildingChecksApi';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useInspectionComponents } from '../../hooks/inspections/useInspectionComponents';
import './BuildingChecksPage.css';
import NumberInput from '../../Components/NumberInput';

const EditBuildingCheckPage: React.FC = () => {
    const { id: buildingId, checkKey } = useParams<{ id: string; checkKey: string }>();
    const navigate = useNavigate();
    const { currentBuilding } = useBuilding();
    const { getTranslation } = useBuildComponentTypes();

    useDocumentTitle(currentBuilding?.summary?.name ? `Redigera aktivitet - ${currentBuilding.summary.name}` : 'Redigera aktivitet');

    // Data state
    const [template, setTemplate] = useState<BuildingCheckTemplate | null>(null);
    const [check, setCheck] = useState<BuildingCheck | null>(null);
    const [loading, setLoading] = useState(true);

    // Form state (editable fields only)
    const [label, setLabel] = useState<string>('');
    const [buildingNotes, setBuildingNotes] = useState<string>('');
    const [useCustomInterval, setUseCustomInterval] = useState(false);
    const [customIntervalValue, setCustomIntervalValue] = useState<number>(1);
    const [customIntervalUnit, setCustomIntervalUnit] = useState<IntervalUnit>('months');
    const [customCost, setCustomCost] = useState<string>('');

    // UI state
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

    // The check's component (fixed once written), or Skickbedömning's group, as it is today
    const { components, loading: componentsLoading } = useInspectionComponents({
        buildingKey: buildingId || '',
        building: currentBuilding,
        componentKey: check?.componentKey,
        baseTypeGroup: template?.componentBaseTypeGroup
    });

    // Load template and check data
    useEffect(() => {
        const loadData = async () => {
            if (!buildingId || !checkKey) return;

            try {
                const [checks, templates] = await Promise.all([
                    buildingChecksApi.loadBuildingCheckInstances(buildingId),
                    buildingChecksApi.loadTemplates()
                ]);
                const checkData = checks[checkKey];
                if (!checkData) {
                    setAlert({ type: 'danger', message: 'Aktiviteten hittades inte' });
                    setLoading(false);
                    return;
                }
                const templateData = templates[checkData.templateId];
                if (!templateData) {
                    setAlert({ type: 'danger', message: 'Mallen hittades inte' });
                    setLoading(false);
                    return;
                }

                setTemplate(templateData);
                setCheck(checkData);

                // Initialize form with existing values
                setLabel(checkData.label || '');
                setBuildingNotes(checkData.buildingNotes || '');
                if (checkData.customInterval) {
                    setUseCustomInterval(true);
                    setCustomIntervalValue(checkData.customInterval.value);
                    setCustomIntervalUnit(checkData.customInterval.unit);
                }
                if (checkData.customCost !== undefined) {
                    setCustomCost(checkData.customCost.toString());
                }

            } catch (error) {
                console.error('Error loading data:', error);
                setAlert({ type: 'danger', message: 'Kunde inte ladda data' });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [buildingId, checkKey]);

    // Handle form submission
    const handleSubmit = async () => {
        if (!template || !check || !buildingId || !checkKey) {
            setAlert({ type: 'danger', message: 'Saknar nödvändig information' });
            return;
        }

        setSaving(true);
        setAlert(null);

        try {
            // Only editable fields; empty text clears the field
            const updates: BuildingCheckChanges = {
                label: label.trim() || null,
                buildingNotes: buildingNotes.trim() || null
            };

            // Custom interval
            if (useCustomInterval) {
                updates.customInterval = {
                    value: customIntervalValue,
                    unit: customIntervalUnit
                };
            } else {
                updates.customInterval = null; // Remove if not using custom
            }

            // Custom cost
            if (customCost.trim()) {
                const costValue = parseFloat(customCost);
                if (!isNaN(costValue) && costValue >= 0) {
                    updates.customCost = costValue;
                }
            } else {
                updates.customCost = null; // Remove if empty
            }

            await buildingChecksApi.updateBuildingCheck(buildingId, checkKey, updates);

            setAlert({ type: 'success', message: 'Ändringar sparade!' });

            // Navigate back after a short delay
            setTimeout(() => {
                navigate(`/building/${buildingId}/checks`);
            }, 1000);

        } catch (error) {
            console.error('Error saving changes:', error);
            setAlert({ type: 'danger', message: 'Kunde inte spara ändringarna. Försök igen.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container fluid className="mt-4 building-check-form-page">
                <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span>Laddar...</span>
                </div>
            </Container>
        );
    }

    if (!template || !check) {
        return (
            <Container fluid className="mt-4 building-check-form-page">
                {alert && (
                    <Alert variant={alert.type}>
                        {alert.message}
                    </Alert>
                )}
                <Link to={`/building/${buildingId}/checks`} className="btn btn-secondary">
                    Tillbaka
                </Link>
            </Container>
        );
    }

    return (
        <>
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={[
                        { text: currentBuilding?.summary?.name || 'Byggnad', link: `/building/${buildingId}` },
                        { text: 'Aktiviteter', link: `/building/${buildingId}/checks` },
                        { text: 'Redigera' }
                    ]}
                />
            </div>

            <Container fluid className="mt-4 building-check-form-page">
                <h1 className="page-title mb-3">Redigera aktivitet</h1>

                {alert && (
                    <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                        {alert.message}
                    </Alert>
                )}

                <Card className="building-check-form">
                    <Card.Body className="p-4">
                        {/* Template Info (read-only) */}
                        <Card className="mb-4 bg-light">
                            <Card.Body className="p-3">
                                <h6 className="mb-3">Mallinformation (ej redigerbar)</h6>
                                <p className="mb-2"><strong>Namn:</strong> {template.name}</p>
                                <p className="mb-2"><strong>Beskrivning:</strong> {template.description}</p>
                                <div className="row">
                                    <div className="col-md-6">
                                        <p className="mb-1 small">
                                            <strong>Standardintervall:</strong> {formatInterval(template.interval)}
                                        </p>
                                    </div>
                                    {template.estimatedCost > 0 && (
                                        <div className="col-md-6">
                                            <p className="mb-1 small">
                                                <strong>Standardkostnad:</strong> {template.estimatedCost} kr
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2">
                                    <Badge bg="secondary">
                                        {translateInspectionCategory(template.category)}
                                    </Badge>
                                    {template.regulatoryType && (
                                        <Badge bg="warning" text="dark" className="ms-2">
                                            {translateRegulatoryType(template.regulatoryType)}
                                        </Badge>
                                    )}
                                    {template.componentSpecificType && (
                                        <Badge bg="info" className="ms-2">
                                            Föreslagen komponenttyp: {getTranslation(template.componentSpecificType)}
                                        </Badge>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Label: tells several checks of one template apart */}
                        <Form.Group className="mb-4">
                            <Form.Label>Benämning</Form.Label>
                            <Form.Text className="text-muted d-block mb-2">
                                Ersätter komponentens namn i listor, t.ex. "Elmätare hus A". Utan benämning visas komponentens namn.
                            </Form.Text>
                            <Form.Control value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} />
                        </Form.Group>

                        {/* The component is fixed once written: to move the check, remove it and add a new one */}
                        {(check.componentKey || template.componentBaseTypeGroup) && (
                            <Card className="mb-4 bg-light">
                                <Card.Body className="p-3">
                                    <h6 className="mb-2">{template.componentBaseTypeGroup ? 'Komponenter' : 'Komponent'}</h6>
                                    {componentsLoading ? (
                                        <div className="d-flex align-items-center gap-2 text-muted">
                                            <Spinner animation="border" size="sm" />
                                            <span>Hämtar komponenter...</span>
                                        </div>
                                    ) : components.length === 0 ? (
                                        <p className="text-muted mb-0 small">
                                            {template.componentBaseTypeGroup
                                                ? 'Inga matchande komponenter i byggnaden.'
                                                : 'Komponenten finns inte längre i byggnaden.'}
                                        </p>
                                    ) : (
                                        <ul className="mb-0 ps-3 small">
                                            {components.slice(0, 5).map(c => (
                                                <li key={c.key}>{c.displayName}</li>
                                            ))}
                                            {components.length > 5 && (
                                                <li className="text-muted">
                                                    ...och {components.length - 5} till
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                    {!template.componentBaseTypeGroup && (
                                        <p className="text-muted mb-0 mt-2 small">
                                            Komponenten kan inte bytas. Ta bort kontrollen och lägg till en ny för en annan komponent.
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        <hr className="my-4" />

                        <h5 className="mb-3">Byggnadspecifika anpassningar</h5>

                        {/* Building Notes */}
                        <Form.Group className="mb-4">
                            <Form.Label>Byggnadspecifika anteckningar</Form.Label>
                            <Form.Text className="text-muted d-block mb-2">
                                Läggs till efter mallens beskrivning
                            </Form.Text>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={buildingNotes}
                                onChange={(e) => setBuildingNotes(e.target.value)}
                                placeholder="T.ex. specifika instruktioner för denna byggnad..."
                            />
                        </Form.Group>

                        {/* Custom Interval */}
                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                id="custom-interval"
                                label="Anpassa intervall"
                                checked={useCustomInterval}
                                onChange={(e) => setUseCustomInterval(e.target.checked)}
                            />
                            {useCustomInterval && (
                                <div className="d-flex gap-2 mt-2">
                                    <NumberInput
                                        className="form-control"
                                        min="1"
                                        integer
                                        emptyValue={1}
                                        value={customIntervalValue}
                                        onChange={(value) => setCustomIntervalValue(Math.max(1, value))}
                                        style={{ width: '100px' }}
                                    />
                                    <Form.Select
                                        value={customIntervalUnit}
                                        onChange={(e) => setCustomIntervalUnit(e.target.value as IntervalUnit)}
                                        style={{ width: '150px' }}
                                    >
                                        {INTERVAL_UNITS.map(unit => (
                                            <option key={unit.value} value={unit.value}>
                                                {unit.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            )}
                            {!useCustomInterval && (
                                <Form.Text className="text-muted">
                                    Använder mallens standardintervall: {formatInterval(template.interval)}
                                </Form.Text>
                            )}
                        </Form.Group>

                        {/* Custom Cost */}
                        <Form.Group className="mb-4">
                            <Form.Label>Anpassa kostnad</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={customCost}
                                    onChange={(e) => setCustomCost(e.target.value)}
                                    placeholder={template.estimatedCost?.toString() || '0'}
                                    style={{ width: '150px' }}
                                />
                                <span>kr</span>
                            </div>
                            {!customCost && template.estimatedCost > 0 && (
                                <Form.Text className="text-muted">
                                    Använder mallens standardkostnad: {template.estimatedCost} kr
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Card.Body>

                    <Card.Footer className="d-flex justify-content-end gap-2">
                        <Link to={`/building/${buildingId}/checks`} className="btn btn-secondary">
                            Avbryt
                        </Link>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? 'Sparar...' : 'Spara ändringar'}
                        </Button>
                    </Card.Footer>
                </Card>
            </Container>
        </>
    );
};

export default EditBuildingCheckPage;
